import { useMemo, useState } from "react";
import styles from "./FilterCategories.module.scss";
import { useTranslation } from "react-i18next";
import Icon from "../../../common/Icon/Icon";
import { useAppSelector } from "../../../../redux/hooks";

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  FilterKey,
  FilterState,
  useFiltersContext,
} from "../FilterContext/FilterContext";
import { RootState } from "../../../../redux/store";

type FilterCategory = {
  id: FilterKey;
} & FilterState[0];

const getCategoryKeyFromFilterKey = (key: FilterKey): FilterKey => {
  if (key === "currentAward") return "trackingAward";

  return key;
};


const FilterCategories: React.FC = () => {
  const { filterState, setFilterState } = useFiltersContext();
  const items = useMemo(
    () => mapFilterStateToCategories(filterState),
    [filterState]
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      setFilterState((items) => {
        const oldIndex = items.findIndex((item) => item.key === active.id);
        const newIndex = items.findIndex((item) => item.key === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <div className={styles.categories}>
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          {items.map((data) => (
            <CategoryRowItem key={data.id} data={data} />
          ))}
        </SortableContext>
      </div>
    </DndContext>
  );
};

const CategoryRowItem: React.FC<{
  data: FilterCategory;
}> = ({ data }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: data.id });

  const { t } = useTranslation();

  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging
      ? {
        boxShadow: "0px 2px 15px 0px #000000CC",
        zIndex: 10,
      }
      : {}),
  };

  const { filterState, selectedFilter, setSelectedFilter } =
    useFiltersContext();

  function onCategorySelect() {
    setSelectedFilter(data.id);
  }

  const currentFilterValueCount = useMemo(
    () =>
      filterState.reduce<number>((count, st) => {
        if (getCategoryKeyFromFilterKey(st.key) === data.key)
          return count + st.values.length;

        return count;
      }, 0),
    [data.key, filterState]
  );

  const isActive = data.key === getCategoryKeyFromFilterKey(selectedFilter);

  return (
    <div
      className={`filter_category_row ${styles.row} ${isActive ? "active" : ""}`}
      ref={setNodeRef}
      style={style}
      onClick={onCategorySelect}
    >
      <div className={styles.drag_handle} {...attributes} {...listeners}>
        <Icon
          size={24}
          name="drag_handle_icon"
          color={currentTheme === "light" ? "#707070" : "#AEAEB2"}
        />
      </div>
      {currentFilterValueCount > 0 ? (
        <p className={`selected`}>
          {t(`filter_categories_title.${data.key}`)} ({currentFilterValueCount})
        </p>
      ) : (
        <p>{t(`filter_categories_title.${data.key}`)}</p>
      )}
    </div>
  );
};

const mapFilterStateToCategories = (
  filterState: FilterState
): FilterCategory[] => {
  const userCountryCode = localStorage.getItem("userCountryCode");
  return filterState
    .filter((f) => userCountryCode !== "IN" ? f.key !== "currentAward" : (f.key !== "loa" && f.key !== "currentAward"))
    .map<FilterCategory>((filter) => ({
      id: filter.key,
      ...filter,
    }));
};

export default FilterCategories;
